import { useState } from 'react'

import { Calendar, Check, ChevronsUpDown, Dog, Eye, Ruler } from 'lucide-react'

import { useCheckIns } from '@/components/ReservationContext'
import { useAvailableRooms } from '@/entities/reservation/model/useAvailableRooms'
import { cn } from '@/shared/lib/styles/class-merge'
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/alert'
import { Button } from '@/shared/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card'
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/shared/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'
import { RoomAssignPopover } from '@/widgets/RoomAssignPopover'
import { HotelReservation, PetSize } from '@monorepo/functions/src/types/reservations'

type PetCardProps = {
    pet: HotelReservation['pets'][0]
    onRoomAssign: (room: string) => void
    onSizeChange: (size: PetSize) => void
    reservations: HotelReservation[]
}

function PetCard({ pet, onRoomAssign, onSizeChange, reservations }: PetCardProps) {
    const [openRoom, setOpenRoom] = useState(false)
    const [openSize, setOpenSize] = useState(false)

    const sizes = [
        { value: 'pequeño', label: 'Pequeño' },
        { value: 'mediano', label: 'Mediano' },
        { value: 'grande', label: 'Grande' },
    ]

    return (
        <Card className='mb-4 last:mb-0'>
            <CardHeader className='pb-2'>
                <CardTitle className='flex items-center gap-2 text-lg font-semibold'>
                    <Dog className='h-5 w-5' />
                    {pet.name}
                </CardTitle>
                <CardDescription>
                    {pet.breed} - {pet.sex === 'M' ? '♂️' : '♀️'} - {pet.isNeutered ? 'Castrado/a' : 'No castrado/a'}
                </CardDescription>
            </CardHeader>
            <CardContent className='grid gap-4'>
                <div className='flex items-center gap-4'>
                    <div className='flex-1'>
                        <RoomAssignPopover
                            isOpen={openRoom}
                            setIsOpen={setOpenRoom}
                            selectedRoom={pet.roomNumber}
                            reservations={reservations}
                            onRoomAssign={room => onRoomAssign(room)}
                        />
                    </div>

                    <div className='flex-1'>
                        <Popover open={openSize} onOpenChange={setOpenSize}>
                            <PopoverTrigger asChild>
                                <Button
                                    variant='outline'
                                    role='combobox'
                                    aria-expanded={openSize}
                                    className='min-h-[2.75rem] w-full justify-between'
                                >
                                    <div className='flex items-center gap-2'>
                                        <Ruler className='h-4 w-4 shrink-0' />
                                        <span>
                                            {sizes.find(s => s.value === pet.size)?.label || 'Seleccionar Tamaño'}
                                        </span>
                                    </div>
                                    <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className='w-[--radix-popover-trigger-width] p-0'>
                                <Command>
                                    <CommandInput placeholder='Buscar tamaño...' />
                                    <CommandEmpty>No se encontraron tamaños.</CommandEmpty>
                                    <CommandList>
                                        {sizes.map(size => (
                                            <CommandItem
                                                key={size.value}
                                                value={size.value}
                                                onSelect={() => {
                                                    onSizeChange(size.value as PetSize)
                                                    setOpenSize(false)
                                                }}
                                            >
                                                <Check
                                                    className={cn(
                                                        'mr-2 h-4 w-4',
                                                        pet.size === size.value ? 'opacity-100' : 'opacity-0',
                                                    )}
                                                />
                                                {size.label}
                                            </CommandItem>
                                        ))}
                                    </CommandList>
                                </Command>
                            </PopoverContent>
                        </Popover>
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}

type CheckInsProps = {
    onViewReservation?: (reservation: HotelReservation) => void
}

export function CheckIns({ onViewReservation }: CheckInsProps) {
    const { checkIns, isLoading, handleRoomAssign, handleSizeChange } = useCheckIns()

    const { currentPetsInRooms } = useAvailableRooms(checkIns)

    if (isLoading) {
        return <div className='py-8 text-center text-muted-foreground'>Cargando check-ins...</div>
    }

    if (checkIns.length === 0) {
        return (
            <Alert variant='info' className='mt-2'>
                <Calendar className='h-4 w-4' />
                <AlertTitle>Sin check-ins</AlertTitle>
                <AlertDescription>No hay entradas programadas para el día de hoy.</AlertDescription>
            </Alert>
        )
    }

    return (
        <div className='space-y-6'>
            {checkIns.map(reservation => (
                <Card key={reservation.id} className='overflow-hidden'>
                    <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                        <div>
                            <CardTitle>{reservation.client.name}</CardTitle>
                            <CardDescription>
                                Check-in: {reservation.checkInTime} - {reservation.pets.length} mascotas
                            </CardDescription>
                        </div>
                        {onViewReservation && (
                            <Button
                                variant='outline'
                                size='sm'
                                onClick={() => onViewReservation(reservation)}
                                className='flex items-center gap-2'
                            >
                                <Eye className='h-4 w-4' />
                                Ver Reserva
                            </Button>
                        )}
                    </CardHeader>
                    <CardContent className='grid gap-4'>
                        {reservation.pets.map((pet, index) => (
                            <PetCard
                                key={pet.name + index}
                                pet={pet}
                                onRoomAssign={room => handleRoomAssign(reservation.id, pet.name, room)}
                                onSizeChange={size => handleSizeChange(reservation.id, index, size)}
                                reservations={checkIns}
                            />
                        ))}
                    </CardContent>
                </Card>
            ))}
        </div>
    )
}
